<template>
  <b-modal
    ref="modalGruppeneinkaufHinzufuegen"
    id="modalGruppeneinkaufHinzufuegen"
    size="lg"
    :title="`Gruppeneinkauf der Reise ${reisekuerzel} hinzufügen`"
    @show="onShow"
    @hidden="onHidden"
    centered
    no-close-on-backdrop
    no-close-on-esc
  >
    <b-overlay :show="isLoading">
      <div class="modal-scroll-content">
        <AddRemoveButton @click="addGruppeneinkauf" />

        <div v-for="(grp, index) in gruppeneinkaeufe" :key="index" class="gruppeneinkauf-block">
          <div class="d-flex align-items-center mb-2">
            <h6 class="m-0 font-weight-bold">{{ index + 1 }}. Gruppeneinkauf</h6>
            <Trash2
              v-if="gruppeneinkaeufe.length > 1"
              :size="16"
              class="ml-3"
              style="cursor: pointer; color: black"
              @click="removeGruppeneinkauf(index)"
            />
          </div>

          <div class="fields-table">
            <div class="field-row">
              <div class="field-label">GRP Flughafen</div>
              <div class="field-input">
                <AppSelect
                  :value="grp.flughafenIataCode"
                  sort="none"
                  :options="getVerfuegbareFlughaefen(index)"
                  :internal-search="false"
                  text-field="iataCode"
                  value-field="iataCode"
                  label="GRP Flughafen"
                  placeholder="----"
                  clearable
                  @input="onFlughafenInput($event, grp)"
                  @search-change="onSearchChange"
                  @open="searchFilter = null"
                />
              </div>
            </div>

            <div v-for="field in fieldDefinitions" :key="field.key" class="field-row">
              <div class="field-label">{{ field.label }}</div>
              <div class="field-input">
                <b-input-group v-if="field.type === 'currency'">
                  <b-form-input
                    size="sm"
                    v-model="grp[field.key]"
                    type="number"
                    step="0.01"
                    placeholder="-"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-input-group-text>€</b-input-group-text>
                  </b-input-group-append>
                </b-input-group>

                <b-form-input
                  v-else-if="field.type === 'number'"
                  size="sm"
                  v-model="grp[field.key]"
                  type="number"
                  min="0"
                  step="1"
                  placeholder="-"
                ></b-form-input>

                <b-form-input v-else size="sm" v-model="grp[field.key]" placeholder="-"></b-form-input>
              </div>
            </div>

            <div class="field-row">
              <div class="field-label">Gesamtpreis GRP</div>
              <div class="field-input">
                <h6 class="m-0 font-weight-bold">{{ berechneGesamtpreis(grp) }}</h6>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-overlay>

    <template #modal-footer="{ hide }">
      <b-button class="dialog-btn-secondary" @click="hide">Abbrechen</b-button>
      <b-button
        class="dialog-btn"
        @click="handleSubmit(hide)"
        :disabled="!isSpeichernAktiv"
        variant="primary"
      >
        Speichern
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import AppSelect from '@/components/common/app-select/app-select.vue';
import { mapState } from 'vuex';
import apiService from '@/core/common/services/api.service';
import { Trash2 } from 'lucide-vue';
import AddRemoveButton from '@/components/common/add-remove-button.vue';

const FIELD_DEFINITIONS = [
  { label: 'GRP PNR', key: 'gruppePnr', type: 'text' },
  { label: 'Eingekaufte Pax', key: 'eingekauftePax', type: 'number' },
  { label: 'Flugnr. GRP Hin', key: 'flugnummerGruppeHin', type: 'text' },
  { label: 'Flugnr. GRP Rück', key: 'flugnummerGruppeRueck', type: 'text' },
  { label: 'OB Class', key: 'obClass', type: 'text' },
  { label: 'IB Class', key: 'ibClass', type: 'text' },
  { label: 'Preis Netto', key: 'hauptstreckePreisNetto', type: 'currency' },
  { label: 'INFL Netto', key: 'inlandsflugPreis', type: 'currency' },
  { label: 'Taxes Gesamt', key: 'taxesFlug', type: 'currency' },
];

export default {
  components: {
    AddRemoveButton,
    AppSelect,
    Trash2,
  },
  props: {
    reisekuerzel: { type: String, default: '' },
    reiseterminIds: { type: Array, default: () => [] },
    bereitsVerwendeteFlughaefen: { type: Array, default: () => [] },
  },
  data() {
    return {
      isLoading: false,
      gruppeneinkaeufe: [],
      searchFilter: null,
      fieldDefinitions: FIELD_DEFINITIONS,
    };
  },
  computed: {
    ...mapState({
      flughaefen: state => state.flughaefen.flughaefen.filter(flgh => flgh.iataCode !== 'PNH'),
    }),
    isSpeichernAktiv() {
      return this.gruppeneinkaeufe.length > 0 && this.gruppeneinkaeufe.every(grp => grp.flughafenIataCode);
    },
  },
  methods: {
    createEmptyGruppeneinkauf() {
      const grp = { flughafenIataCode: null };
      this.fieldDefinitions.forEach(field => {
        grp[field.key] = null;
      });
      return grp;
    },
    onShow() {
      this.gruppeneinkaeufe = [this.createEmptyGruppeneinkauf()];
    },
    onHidden() {
      this.gruppeneinkaeufe = [];
    },
    addGruppeneinkauf() {
      this.gruppeneinkaeufe.unshift(this.createEmptyGruppeneinkauf());
    },
    removeGruppeneinkauf(index) {
      this.gruppeneinkaeufe.splice(index, 1);
    },
    onFlughafenInput(iataCode, grp) {
      grp.flughafenIataCode = iataCode ?? null;
      this.searchFilter = null;
    },
    onSearchChange(query) {
      this.searchFilter = query || null;
    },
    getVerfuegbareFlughaefen(currentIndex) {
      const excluded = new Set([
        ...this.bereitsVerwendeteFlughaefen,
        ...this.gruppeneinkaeufe
          .filter((_, i) => i !== currentIndex)
          .map(grp => grp.flughafenIataCode)
          .filter(Boolean),
      ]);

      let airports = this.flughaefen;
      if (this.searchFilter) {
        airports = airports.filter(fh =>
          fh.iataCode.toLowerCase().startsWith(this.searchFilter.toLowerCase())
        );
      } else if (this.reisekuerzel) {
        const country = this.reisekuerzel.substring(0, 2);
        const filtered = airports.filter(fh => fh.country === country);
        if (filtered.length > 0) airports = filtered;
      }

      const verfuegbar = airports.filter(fh => !excluded.has(fh.iataCode));
      const gewaehlt = this.gruppeneinkaeufe[currentIndex]?.flughafenIataCode;
      if (gewaehlt && !verfuegbar.some(fh => fh.iataCode === gewaehlt)) {
        const eintrag = this.flughaefen.find(fh => fh.iataCode === gewaehlt);
        if (eintrag) {
          return [eintrag, ...verfuegbar];
        }
      }
      return verfuegbar;
    },
    berechneGesamtpreis(grp) {
      const preis = Number(grp.hauptstreckePreisNetto) || 0;
      const infl = Number(grp.inlandsflugPreis) || 0;
      const taxes = Number(grp.taxesFlug) || 0;
      const gesamt = preis + infl + taxes;
      return gesamt.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€';
    },
    // Baut das DTO (Data Transfer Object) für die API aus den Formularwerten
    buildDto(grp) {
      const dto = { flughafenIataCode: grp.flughafenIataCode };
      this.fieldDefinitions.forEach(field => {
        const value = grp[field.key];
        if (field.type === 'number' || field.type === 'currency') {
          dto[field.key] = value != null && value !== '' ? Number(value) : null;
        } else {
          dto[field.key] = value || null;
        }
      });
      return dto;
    },
    // Speichert alle Gruppeneinkäufe nacheinander
    // Für jeden Eintrag wird ein eigener API-Call gemacht, weil jeder einen anderen Flughafen hat
    async handleSubmit(hide) {
      this.isLoading = true;
      try {
        for (const grp of this.gruppeneinkaeufe) {
          await apiService.post('Reisetermin/bulkCreateGruppeneinkauf', {
            reiseterminIds: this.reiseterminIds,
            gruppeneinkaufDto: this.buildDto(grp),
          });
        }

        this.$emit('gruppeneinkaufHinzugefuegt');
        hide();
      } catch (error) {
        console.error('Fehler beim Hinzufügen der Gruppeneinkäufe:', error);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-scroll-content {
  max-height: 70vh;
  overflow-y: auto;
}
.gruppeneinkauf-block {
  margin-top: 16px;
}
.fields-table {
  border-top: 1px solid var(--border);
}
.field-row {
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--border);
  padding: 8px 0;
}
.field-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}
.field-label {
  width: 160px;
  flex-shrink: 0;
  font-weight: 500;
  font-size: 14px;
  padding-left: 8px;
}
.field-input {
  flex: 1;
}
.field-input :deep(.input-group-text) {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}
</style>
